import { Controller } from "@hotwired/stimulus"
import {
  beginLessonSearchLoadingProgress,
  beginLessonResultsLoading,
  endLessonSearchLoadingProgress,
  endLessonResultsLoading,
  updateLessonSearchLoadingQuery,
} from '../lib/lesson_search_loading'

// Wraps the videos search form and, on mobile, the full-screen filter modal.
//
// While the modal is open, filter changes update results first, then request
// predictive facet counts independently.
// The query does not: it searches on Enter, or on "Show results" if the box
// still has uncommitted text. "Cancel" discards that and returns to
// where you were when you opened the modal.
//
// Rather than navigate back to a cached URL on cancel — a full server round-trip
// that feels janky next to the live in-modal updates — we snapshot the committed
// state when the modal opens and restore it from the DOM instantly on cancel. No
// network, no flash.
export default class extends Controller {
  static targets = [ 'wrapper' ];

  connect(){
    this.element.modal = this;
    this.boundPrepareForCache = this.prepareForCache.bind(this);
    document.addEventListener('turbo:before-cache', this.boundPrepareForCache);

    // Arriving from the global header search icon on another page: the link
    // carries ?open_search=1 so we open the modal on load. Strip the flag
    // from the URL first so a refresh or Cancel-restore doesn't reopen it.
    const params = new URLSearchParams(window.location.search);
    if (params.get('open_search')) {
      params.delete('open_search');
      const query = params.toString();
      const cleaned = window.location.pathname + (query ? `?${query}` : '');
      window.history.replaceState(window.history.state, '', cleaned);
      this.openModal();
    }
  }

  disconnect() {
    document.removeEventListener('turbo:before-cache', this.boundPrepareForCache);
    window.cancelAnimationFrame(this.entranceFrame);
    window.cancelAnimationFrame(this.cacheRestoreFrame);
    clearTimeout(this.closeTimer);
    this.closeTimer = null;
    this.stopVisualViewportTracking();
    this.unlockDocumentScroll();
  }

  open(e) {
    e.preventDefault();
    this.openModal();
  }

  openModal() {
    // Always open on the search screen (Screen A), even if the last session
    // committed from the filters screen. Done before snapshotting so the
    // captured HTML — and therefore cancel — also lands on Screen A.
    const screens = this.wrapperTarget.querySelector('[data-controller~="modal-screens"]');
    if (screens) screens.dataset.screen = 'search';
    // Capture the committed state before the user starts exploring. These are
    // exactly the regions the index.turbo_stream response replaces, plus the
    // query value (which the stream never re-renders) and the current URL.
    // The cold entry modal (in the layout, off the results page) has no
    // #search_results to snapshot — capture just the wrapper + query there.
    this.snapshot = {
      wrapperHTML: this.wrapperTarget.innerHTML,
      resultsHTML: this.resultsElement ? this.resultsElement.innerHTML : null,
      query: this.queryElement ? this.queryElement.value : null,
      url: window.location.href,
      scrollY: window.scrollY,
    };
    // Live submits during the session should replace (not stack) history, so the
    // back button and cancel both land on the committed URL rather than a
    // half-explored query. search_controller#updateURL reads this flag.
    if (this.formElement) this.formElement.dataset.modalSession = 'true';

    // A close mid-animation leaves a pending re-hide timer; cancel it so a quick
    // reopen doesn't get yanked back to display:none underneath the slide-in.
    if (this.closeTimer) { clearTimeout(this.closeTimer); this.closeTimer = null; }

    // Mobile Safari only opens its software keyboard when focus happens inside
    // the original tap. Focus an empty query against the panel's stable layout
    // box while that preparation frame is clipped, then start the translated
    // entrance on the following frame. This is the same pattern as the mobile
    // profile text editor: autofocus without letting focus cancel the slide-in.
    const query = this.queryElement;
    // Read the rendered presentation rather than viewport dimensions: the same
    // wrapper is display:none while parked as a mobile drawer, but md:block as
    // the persistent desktop sidebar.
    const opensAsDrawer = getComputedStyle(this.wrapperTarget).display === 'none';
    const shouldFocusQuery = opensAsDrawer && query && query.value.trim() === '';

    if (opensAsDrawer) {
      this.lockDocumentScroll();
      this.startVisualViewportTracking();
    }

    window.cancelAnimationFrame(this.entranceFrame);
    this.wrapperTarget.classList.toggle('search-modal--focus-preparing', shouldFocusQuery);
    this.wrapperTarget.classList.remove('hidden');

    if (shouldFocusQuery) {
      void this.wrapperTarget.offsetWidth;
      query.focus({ preventScroll: true });

      this.entranceFrame = window.requestAnimationFrame(() => {
        this.wrapperTarget.classList.remove('search-modal--focus-preparing');
        this.wrapperTarget.classList.add('is-open', 'search-modal--focus-entrance');
        // Some browsers return focus to the tapped opener after its click
        // finishes. The synchronous focus above is what authorises the iOS
        // keyboard; this second call keeps the query active once the entrance
        // frame has started.
        query.focus({ preventScroll: true });
        this.entranceFrame = null;
      });
    } else {
      void this.wrapperTarget.offsetWidth;
      this.wrapperTarget.classList.add('is-open');
    }

    // Mobile defers predictive counts until the user actually opens search.
    // Desktop loads them after paint from facet_counts_controller itself.
    this.element.dispatchEvent(new CustomEvent('facets:load'));
  }

  // "Show results" — keep the live filter state and close. A query that was
  // typed but not yet submitted (Enter) goes out with this tap, so
  // closing the modal doesn't leave the box and the results disagreeing.
  // Cancel passes { skipSearch: true } after restoring the snapshot; that
  // restore must stay local, with no request.
  close(event) {
    const form = this.formElement;
    const submitting = !event?.skipSearch && this.searchController?.queryIsDirty();
    if (submitting) {
      // Leave modalSession set until this request finishes so updateURL still
      // replaces history, the same as Enter while the modal is open.
      form?.addEventListener('turbo:submit-end', () => {
        delete form.dataset.modalSession;
      }, { once: true });
      this.searchController.submitQuery();
    } else if (form && !event?.preserveSession) {
      delete form.dataset.modalSession;
    }
    // Slide back out, then restore display:none once the panel is off-screen so
    // the parked wrapper can't add horizontal overflow. The delay matches the
    // 150ms CSS transition; the guard in openModal clears it on a fast reopen.
    window.cancelAnimationFrame(this.entranceFrame);
    this.entranceFrame = null;
    this.wrapperTarget.classList.remove(
      'is-open',
      'search-modal--focus-preparing',
      'search-modal--focus-entrance'
    );
    // A search that already replaced the list scrolled to the top behind the
    // sheet. Scroll again once the sheet is gone: iOS can drop a scroll that
    // happened while a fixed overlay covered the page.
    const scrollToTop = event?.scrollToTop ||
      (!event?.skipSearch && this.resultsChangedSinceOpen());
    this.closeTimer = setTimeout(() => {
      this.wrapperTarget.classList.add('hidden');
      this.stopVisualViewportTracking();
      this.unlockDocumentScroll();
      if (scrollToTop) window.scrollTo(0, 0);
      this.closeTimer = null;
    }, 150);
  }

  // Enter on mobile is an explicit request to see the new result set, not an
  // invitation to wait inside the filter sheet. Submit once, slide the sheet
  // away immediately, and show the shared lesson-search skeleton.
  submitQueryAndClose() {
    const form = this.formElement;
    if (!form || !this.searchController) return;

    form.addEventListener('turbo:submit-end', () => {
      delete form.dataset.modalSession;
      window.scrollTo(0, 0);
    }, { once: true });

    this.searchController.submitQuery();
    this.close({ skipSearch: true, preserveSession: true, scrollToTop: true });
  }

  // "Show results" in the cold modal — there's nothing to show in place, so
  // navigate to the results page carrying the filters the form has collected.
  showResults(e) {
    e?.preventDefault();
    const form = this.formElement;
    if (!form) return;

    // The CTA can be tapped without pressing Enter first. Commit the query and
    // relevance ordering locally, but do not submit the embedded/count-only
    // request — the following Turbo visit is the one and only search request.
    if (this.searchController?.queryIsDirty()) {
      this.searchController.prepareQuerySubmit();
    }

    const params = new URLSearchParams(new FormData(form));
    params.delete('embedded'); // the live-count-only marker; not a filter
    const action = form.getAttribute('action') || '/videos';
    const url = `${action}?${params.toString()}`;

    this.beginColdNavigation();
    if (window.Turbo) {
      window.Turbo.visit(url);
    } else {
      window.location.assign(url);
    }
  }

  beginColdNavigation(query = this.queryElement?.value.trim()) {
    this.coldNavigationPending = true;
    updateLessonSearchLoadingQuery(query);
    this.wrapperTarget.classList.add('is-searching');
    this.wrapperTarget.setAttribute('aria-busy', 'true');
    beginLessonSearchLoadingProgress(this.wrapperTarget);
  }

  // Suggestion links perform a full Turbo visit rather than an in-place form
  // submit. Enter the pending-results state synchronously so a cold,
  // fingerprinted URL feels exactly like a cached recent search. On mobile the
  // sheet is removed immediately to reveal the results-shaped loader beneath;
  // a cold modal has no results region, so it becomes the loader itself.
  beginSuggestionNavigation(event) {
    if (!this.primaryNavigationClick(event)) return;

    const url = event.currentTarget?.href;
    if (!url) return;

    // Do not rely on Turbo's document-level click observer here. Mobile Safari
    // can fall through to a native document navigation after this handler has
    // synchronously hidden the modal. That freezes the departing page (and its
    // progress timer) until the response arrives. Claim the click and start the
    // Turbo visit explicitly so the interstitial remains live throughout.
    event.preventDefault();
    const query = event.currentTarget?.dataset?.lessonSearchLoadingQuery?.trim();
    this.queryElement?.blur();
    if (!this.resultsElement) {
      this.beginColdNavigation(query);
      this.visitSuggestionResults(url);
      return;
    }

    this.resultsNavigationPending = true;
    beginLessonResultsLoading(query);
    if (this.isOpen) this.hideForResultsNavigation();
    window.scrollTo(0, 0);
    this.visitSuggestionResults(url);
  }

  visitSuggestionResults(url) {
    if (window.Turbo) {
      window.Turbo.visit(url);
    } else {
      window.location.assign(url);
    }
  }

  hideForResultsNavigation() {
    window.cancelAnimationFrame(this.entranceFrame);
    this.entranceFrame = null;
    clearTimeout(this.closeTimer);
    this.closeTimer = null;
    this.wrapperTarget.classList.remove(
      'is-open',
      'search-modal--focus-preparing',
      'search-modal--focus-entrance'
    );
    this.wrapperTarget.classList.add('hidden');
    this.stopVisualViewportTracking();
    this.unlockDocumentScroll();
  }

  primaryNavigationClick(event) {
    if (event.button !== undefined && event.button !== 0) return false;
    return !(event.metaKey || event.ctrlKey || event.shiftKey || event.altKey);
  }

  endColdNavigation() {
    this.coldNavigationPending = false;
    this.resultsNavigationPending = false;
    this.wrapperTarget.classList.remove('is-searching');
    this.wrapperTarget.removeAttribute('aria-busy');
    endLessonSearchLoadingProgress(this.wrapperTarget);
    endLessonResultsLoading();
  }

  // Turbo snapshots the departing page after the loading shell is shown. Never
  // cache that transient state, or Back would restore an already-busy modal.
  // The cold/global modal is turbo-permanent, so also clear its query before
  // caching. The results-page URL is the source of truth for committed search
  // state; otherwise returning Home can resurrect the query that originally
  // launched the search, even after it was cleared on the results page.
  prepareForCache() {
    const restorePendingNavigation = this.coldNavigationPending;
    const restorePendingResultsNavigation = this.resultsNavigationPending;
    const pendingRoot = restorePendingNavigation
      ? this.wrapperTarget
      : this.resultsElement?.parentElement;
    const pendingQuery = pendingRoot
      ?.querySelector('[data-lesson-search-loading-query]')
      ?.textContent?.trim();

    this.endColdNavigation();
    this.resetColdQuery();
    window.cancelAnimationFrame(this.entranceFrame);
    this.entranceFrame = null;
    this.wrapperTarget.classList.remove(
      'is-open',
      'search-modal--focus-preparing',
      'search-modal--focus-entrance'
    );
    this.wrapperTarget.classList.add('hidden');
    clearTimeout(this.closeTimer);
    this.closeTimer = null;
    this.stopVisualViewportTracking();
    this.unlockDocumentScroll();

    // Turbo snapshots the page synchronously after `turbo:before-cache`, but
    // it can do so before the navigation request has completed. Leave the
    // snapshot clean, then restore the live cold-modal loader on the next frame
    // so the user never spends the request looking at a stale suggestion list.
    if (restorePendingNavigation || restorePendingResultsNavigation) {
      this.cacheRestoreFrame = window.requestAnimationFrame(() => {
        this.cacheRestoreFrame = null;
        if (this.element.isConnected === false) return;

        if (restorePendingNavigation) {
          this.wrapperTarget.classList.remove('hidden');
          this.wrapperTarget.classList.add('is-open');
          this.lockDocumentScroll();
          this.startVisualViewportTracking();
          this.beginColdNavigation(pendingQuery);
        } else {
          this.resultsNavigationPending = true;
          beginLessonResultsLoading(pendingQuery);
        }
      });
    }
  }

  resetColdQuery() {
    if (!this.element.hasAttribute('data-cold-modal') || !this.queryElement) return;

    this.queryElement.value = '';
    this.searchController?.toggleClearButton();
    this.searchController?.resetOrderForQueryChange();
    this.searchController?.rememberQuery();
  }

  startVisualViewportTracking() {
    if (this.visualViewport || !window.visualViewport) return;

    this.visualViewport = window.visualViewport;
    this.boundVisualViewportChange = this.syncVisualViewport.bind(this);
    this.visualViewport.addEventListener('resize', this.boundVisualViewportChange);
    this.visualViewport.addEventListener('scroll', this.boundVisualViewportChange);
    this.syncVisualViewport();
  }

  syncVisualViewport() {
    if (!this.visualViewport) return;

    this.wrapperTarget.style.setProperty(
      '--search-modal-viewport-height',
      `${this.visualViewport.height}px`
    );
    this.wrapperTarget.style.setProperty(
      '--search-modal-viewport-top',
      `${this.visualViewport.offsetTop}px`
    );
  }

  stopVisualViewportTracking() {
    if (!this.visualViewport) return;

    this.visualViewport.removeEventListener('resize', this.boundVisualViewportChange);
    this.visualViewport.removeEventListener('scroll', this.boundVisualViewportChange);
    this.wrapperTarget.style.removeProperty('--search-modal-viewport-height');
    this.wrapperTarget.style.removeProperty('--search-modal-viewport-top');
    this.boundVisualViewportChange = null;
    this.visualViewport = null;
  }

  lockDocumentScroll() {
    if (this.documentScrollLocked) return;

    this.documentScrollLocked = true;
    this.documentScrollY = window.scrollY;
    this.boundModalTouchStart = this.handleModalTouchStart.bind(this);
    this.boundModalTouchMove = this.handleModalTouchMove.bind(this);
    this.boundModalTouchEnd = this.resetModalTouch.bind(this);
    this.wrapperTarget.addEventListener('touchstart', this.boundModalTouchStart, { passive: true });
    this.wrapperTarget.addEventListener('touchmove', this.boundModalTouchMove, { passive: false });
    this.wrapperTarget.addEventListener('touchend', this.boundModalTouchEnd, { passive: true });
    this.wrapperTarget.addEventListener('touchcancel', this.boundModalTouchEnd, { passive: true });
    document.documentElement.classList.add('search-modal-scroll-locked');
    document.body.classList.add('search-modal-scroll-locked');
    document.body.style.top = `-${this.documentScrollY}px`;
  }

  unlockDocumentScroll() {
    if (!this.documentScrollLocked) return;

    const scrollY = this.documentScrollY || 0;
    this.wrapperTarget.removeEventListener('touchstart', this.boundModalTouchStart);
    this.wrapperTarget.removeEventListener('touchmove', this.boundModalTouchMove);
    this.wrapperTarget.removeEventListener('touchend', this.boundModalTouchEnd);
    this.wrapperTarget.removeEventListener('touchcancel', this.boundModalTouchEnd);
    this.boundModalTouchStart = null;
    this.boundModalTouchMove = null;
    this.boundModalTouchEnd = null;
    this.resetModalTouch(true);
    document.documentElement.classList.remove('search-modal-scroll-locked');
    document.body.classList.remove('search-modal-scroll-locked');
    document.body.style.removeProperty('top');
    // The lock's fixed positioning comes from CSS. Force that class removal to
    // take effect before restoring the offset; otherwise Chromium and iOS can
    // discard scrollTo while the body still belongs to the fixed layout tree.
    void document.body.offsetHeight;
    this.documentScrollLocked = false;
    this.documentScrollY = null;
    window.scrollTo(0, scrollY);
  }

  // iOS can still pan and rubber-band its visual viewport when html/body use
  // overflow:hidden. That produces a root scrollbar and repeatedly changes the
  // VisualViewport offset, making the fixed search header appear to shake. The
  // suggestion list is the sheet's sole scroll owner: block every other pan,
  // and block the list itself when it has no overflow or reaches either edge.
  handleModalTouchStart(event) {
    this.releaseSuggestionElasticity(true);
    this.modalTouchY = event.touches.length === 1 ? event.touches[0].clientY : null;
  }

  handleModalTouchMove(event) {
    if (!event.cancelable || event.touches.length !== 1) return;

    const currentY = event.touches[0].clientY;
    const previousY = this.modalTouchY ?? currentY;
    const deltaY = currentY - previousY;
    const target = event.target instanceof Element ? event.target : event.target?.parentElement;
    const suggestions = target?.closest('.search-suggestions');
    const maxScrollTop = suggestions ? suggestions.scrollHeight - suggestions.clientHeight : 0;
    const cannotScroll = !suggestions || maxScrollTop <= 1;
    const pullingPastTop = suggestions && suggestions.scrollTop <= 0 && deltaY > 0;
    const pullingPastBottom = suggestions && suggestions.scrollTop >= maxScrollTop - 1 && deltaY < 0;
    const activeElasticity = suggestions && this.elasticSuggestions === suggestions;

    if (activeElasticity) {
      this.elasticDistance += deltaY;
      if (this.elasticBoundary === 'top') this.elasticDistance = Math.max(0, this.elasticDistance);
      if (this.elasticBoundary === 'bottom') this.elasticDistance = Math.min(0, this.elasticDistance);

      if (this.elasticDistance === 0) {
        this.releaseSuggestionElasticity(true);
      } else {
        event.preventDefault();
        this.renderSuggestionElasticity();
      }
    } else if (cannotScroll || pullingPastTop || pullingPastBottom) {
      event.preventDefault();

      if (suggestions && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        const boundary = cannotScroll ? 'free' : (pullingPastTop ? 'top' : 'bottom');
        this.startSuggestionElasticity(suggestions, boundary, deltaY);
      }
    }

    this.modalTouchY = currentY;
  }

  resetModalTouch(immediate = false) {
    this.modalTouchY = null;
    this.releaseSuggestionElasticity(immediate === true);
  }

  startSuggestionElasticity(suggestions, boundary, distance) {
    this.elasticSuggestions = suggestions;
    this.elasticBoundary = boundary;
    this.elasticDistance = distance;
    suggestions.classList.add('is-elastic');
    this.renderSuggestionElasticity();
  }

  renderSuggestionElasticity() {
    if (!this.elasticSuggestions) return;

    // Match WebKit-style rubber-banding: the rows initially follow about half
    // the finger's travel, then gain resistance in proportion to the available
    // list height. Unlike a small fixed cap, this keeps responding throughout
    // a long swipe while remaining asymptotically constrained to the surface.
    const dimension = Math.max(1, this.elasticSuggestions.clientHeight);
    const distance = Math.abs(this.elasticDistance);
    const resistance = 0.55;
    const offsetMagnitude = (1 - (1 / ((distance * resistance / dimension) + 1))) * dimension;
    const offset = Math.sign(this.elasticDistance) * offsetMagnitude;
    this.elasticSuggestions.style.setProperty('--search-suggestions-elastic-offset', `${offset}px`);
  }

  releaseSuggestionElasticity(immediate = false) {
    clearTimeout(this.elasticReboundTimer);
    const suggestions = this.elasticSuggestions ||
      this.wrapperTarget.querySelector('.search-suggestions.is-elastic-rebounding');

    this.elasticSuggestions = null;
    this.elasticBoundary = null;
    this.elasticDistance = 0;
    if (!suggestions) return;

    suggestions.classList.remove('is-elastic');
    if (immediate) {
      suggestions.classList.remove('is-elastic-rebounding');
      suggestions.style.removeProperty('--search-suggestions-elastic-offset');
      return;
    }

    suggestions.classList.add('is-elastic-rebounding');
    suggestions.style.setProperty('--search-suggestions-elastic-offset', '0px');
    this.elasticReboundTimer = setTimeout(() => {
      suggestions.classList.remove('is-elastic-rebounding');
      suggestions.style.removeProperty('--search-suggestions-elastic-offset');
      this.elasticReboundTimer = null;
    }, 380);
  }

  // "Cancel" — restore the committed state captured on open, instantly.
  cancel() {
    if (this.snapshot) {
      this.wrapperTarget.innerHTML = this.snapshot.wrapperHTML;
      if (this.resultsElement && this.snapshot.resultsHTML !== null) {
        this.resultsElement.innerHTML = this.snapshot.resultsHTML;
      }
      // The query field lives in the wrapper but isn't re-rendered by submits,
      // so its value can drift from the restored markup — set it explicitly.
      if (this.queryElement) this.queryElement.value = this.snapshot.query;
      // The restored box is the committed query again. Otherwise a later
      // Show results tap treats the restored text as uncommitted and searches.
      this.searchController?.rememberQuery();
      window.history.replaceState(window.history.state, null, this.snapshot.url);
      // The in-modal search snapped the page to the top. Put the list back
      // where it was, since the results themselves were just restored.
      window.scrollTo(0, this.snapshot.scrollY || 0);
    }
    this.close({ skipSearch: true });
  }

  get searchController() {
    const form = this.formElement;
    if (!form) return null;

    return this.application.getControllerForElementAndIdentifier(form, 'search');
  }

  get formElement() {
    return this.element.querySelector('form');
  }

  get resultsElement() {
    return document.getElementById('search_results');
  }

  // Desktop renders the same wrapper as a persistent sidebar. `is-open` is set
  // only when the mobile sheet is actively covering the page, making it a more
  // reliable signal than viewport APIs in resized/webview environments.
  get isOpen() {
    return this.wrapperTarget.classList.contains('is-open');
  }

  get queryElement() {
    return this.element.querySelector('[name="query"]');
  }

  resultsChangedSinceOpen() {
    if (!this.snapshot || this.snapshot.resultsHTML === null || !this.resultsElement) return false;

    return this.resultsElement.innerHTML !== this.snapshot.resultsHTML;
  }
}
